import React, { useEffect, useState, useRef } from "react";
import {
  PencilIcon,
  TrashIcon,
  PlusIcon,
  PowerIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import useProxyStore from "../stores/proxies/proxies";
import ConfirmationModal from "../components/modal/ConfirmationModal";
import { useToast } from "../context/toastContext";

export default function ProxyManagement() {
  const { addToast } = useToast();
  const { proxies, pagination, loadingList, loadingAction, error, actions } =
    useProxyStore();

  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);

  const [confirmModal, setConfirmModal] = useState({
    show: false,
    title: "",
    message: "",
    onConfirm: null,
  });

  const modalRef = useRef(null);

  const [form, setForm] = useState({
    name: "",
    url: "",
    port: "",
    username: "",
    password: "",
    is_active: true,
  });

  // Load initial list
  useEffect(() => {
    actions.fetchList({ page: 1, size: 10 });
  }, []);

  // ========================================================
  // CLICK OUTSIDE TO CLOSE MODAL
  // ========================================================
  useEffect(() => {
    if (!showForm) return;

    const handleOutside = (e) => {
      if (modalRef.current && !modalRef.current.contains(e.target)) {
        setShowForm(false);
      }
    };

    const handleEsc = (e) => {
      if (e.key === "Escape") setShowForm(false);
    };

    document.addEventListener("mousedown", handleOutside);
    document.addEventListener("keydown", handleEsc);

    return () => {
      document.removeEventListener("mousedown", handleOutside);
      document.removeEventListener("keydown", handleEsc);
    };
  }, [showForm]);

  // ========================================================
  // FORM OPENERS
  // ========================================================
  const handleCreate = () => {
    setEditing(null);
    setForm({
      name: "",
      url: "",
      port: "",
      username: "",
      password: "",
      is_active: true,
    });
    setShowForm(true);
  };

  const handleEdit = (proxy) => {
    setEditing(proxy.id);
    setForm({
      name: proxy.name,
      url: proxy.url,
      port: proxy.port,
      username: proxy.username,
      password: proxy.password,
      is_active: proxy.is_active,
    });
    setShowForm(true);
  };

  // ========================================================
  // SUBMIT CREATE / UPDATE
  // ========================================================
  const handleSubmit = async () => {
    try {
      if (editing) {
        await actions.updateProxy(editing, form);
        addToast("success", "Proxy updated successfully!");
      } else {
        await actions.createProxy(form);
        addToast("success", "Proxy created successfully!");
      }
      setShowForm(false);
    } catch {
      addToast("error", "Something went wrong!");
    }
  };

  // ========================================================
  // DELETE WITH CONFIRMATION MODAL
  // ========================================================
  const handleDelete = (proxy) => {
    setConfirmModal({
      show: true,
      title: "Delete Proxy?",
      message: `Are you sure you want to delete "${proxy.name}"?`,
      onConfirm: async () => {
        try {
          await actions.deleteProxy(proxy.id);
          addToast("success", "Proxy deleted");
        } catch {
          addToast("error", "Failed to delete proxy");
        }
        setConfirmModal({ show: false });
      },
    });
  };

  // ========================================================
  // TOGGLE ACTIVE WITH CONFIRMATION MODAL
  // ========================================================
  const handleToggle = (proxy) => {
    setConfirmModal({
      show: true,
      title: proxy.is_active ? "Deactivate Proxy?" : "Activate Proxy?",
      message: `Are you sure you want to ${
        proxy.is_active ? "deactivate" : "activate"
      } "${proxy.name}"?`,
      onConfirm: async () => {
        try {
          await actions.toggleActive(proxy.id, !proxy.is_active);
          addToast(
            "success",
            proxy.is_active ? "Proxy deactivated" : "Proxy activated"
          );
        } catch {
          addToast("error", "Update failed");
        }
        setConfirmModal({ show: false });
      },
    });
  };

  // ========================================================
  // RENDER UI
  // ========================================================
  return (
    <div className="bg-white p-6 rounded shadow relative">
      {/* HEADER */}
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-xl font-semibold">Proxy Management</h1>

        <button
          onClick={handleCreate}
          className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded"
        >
          <PlusIcon className="w-5 h-5" /> Add Proxy
        </button>
      </div>

      {/* ERROR */}
      {error && <p className="text-red-500 mb-4">{error}</p>}

      {/* TABLE */}
      <div className="overflow-x-auto">
        <table className="w-full border">
          <thead className="bg-gray-200">
            <tr>
              <th className="p-2">Name</th>
              <th className="p-2">URL</th>
              <th className="p-2">Port</th>
              <th className="p-2">User</th>
              <th className="p-2">Active</th>
              <th className="p-2 w-32">Actions</th>
            </tr>
          </thead>

          <tbody>
            {loadingList ? (
              <tr>
                <td colSpan="6" className="p-4 text-center">
                  Loading...
                </td>
              </tr>
            ) : proxies.length === 0 ? (
              <tr>
                <td colSpan="6" className="p-4 text-center">
                  No proxies found.
                </td>
              </tr>
            ) : (
              proxies.map((proxy) => (
                <tr key={proxy.id} className="border-t">
                  <td className="p-2">{proxy.name}</td>
                  <td className="p-2">{proxy.url}</td>
                  <td className="p-2">{proxy.port}</td>
                  <td className="p-2">{proxy.username}</td>

                  <td className="p-2">
                    <button
                      disabled={loadingAction}
                      onClick={() => handleToggle(proxy)}
                      className={`flex items-center gap-1 mx-auto ${
                        proxy.is_active ? "text-green-600" : "text-gray-400"
                      }`}
                    >
                      <PowerIcon className="w-5 h-5" />
                      {proxy.is_active ? "Active" : "Inactive"}
                    </button>
                  </td>

                  <td className="p-2 flex justify-center gap-3">
                    <button
                      disabled={loadingAction}
                      onClick={() => handleEdit(proxy)}
                      className="text-blue-600 hover:text-blue-800"
                    >
                      <PencilIcon className="w-5 h-5" />
                    </button>

                    <button
                      disabled={loadingAction}
                      onClick={() => handleDelete(proxy)}
                      className="text-red-600 hover:text-red-800"
                    >
                      <TrashIcon className="w-5 h-5" />
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* ======================================================
          FORM MODAL — FADE & SCALE TRANSITION
      ====================================================== */}
      {showForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm transition-opacity duration-200">
          <div
            ref={modalRef}
            className="bg-white p-6 rounded shadow w-[600px] animate-[fadeInScale_.25s_ease]"
          >
            {/* HEADER */}
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-semibold">
                {editing ? "Edit Proxy" : "Create Proxy"}
              </h2>

              <button
                onClick={() => setShowForm(false)}
                className="text-gray-500 hover:text-gray-800"
              >
                <XMarkIcon className="w-6 h-6" />
              </button>
            </div>

            {/* FORM FIELDS */}
            <div className="space-y-3">
              {["name", "url", "port", "username", "password"].map((field) => (
                <div key={field}>
                  <label className="block text-sm text-left mb-2 capitalize">
                    {field}
                  </label>
                  <input
                    type={field === "password" ? "password" : "text"}
                    className="border p-2 rounded w-full"
                    value={form[field]}
                    onChange={(e) =>
                      setForm({ ...form, [field]: e.target.value })
                    }
                  />
                </div>
              ))}
            </div>

            {/* BUTTONS */}
            <div className="flex justify-end gap-3 mt-5">
              <button
                onClick={() => setShowForm(false)}
                className="px-4 py-2 bg-gray-300 rounded"
              >
                Cancel
              </button>
              <button
                disabled={loadingAction}
                onClick={handleSubmit}
                className="px-4 py-2 bg-blue-600 text-white rounded"
              >
                {loadingAction ? "Loading..." : editing ? "Update" : "Create"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* CONFIRMATION MODAL */}
      <ConfirmationModal
        show={confirmModal.show}
        title={confirmModal.title}
        message={confirmModal.message}
        onConfirm={confirmModal.onConfirm}
        onCancel={() => setConfirmModal({ show: false })}
        isProcessing={loadingAction}
      />
    </div>
  );
}
